import classNames from 'classnames';
import { capitalize } from 'lodash';
import { memo, useCallback, useState } from 'react';
import { usePromise } from 'react-use';
import { useDebounce } from 'use-debounce';

import { type SortByOption as TagsSortByOption } from '@air/api/types';

import { getTagErrorMessage } from '~/components/Modals/Tags/utils';
import { TagsSelect, type TagsSelectProps } from '~/components/Tags/TagsSelect';
import { convertTagToSelectOption, type TagBase, type TagSelectOption } from '~/components/Tags/utils';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCreateTag } from '~/swr-hooks/tags/useCreateTag';
import { usePrivateTags } from '~/swr-hooks/usePrivateTags';
import { canCreateTag } from '~/utils/permissions/workspacePermissions';

const SEARCH_DEBOUNCE_MS = 200;

export type PrivateTagsSelectProps = {
  selectedTags?: TagBase[];
  onSelectedTagsChanged: (newTags: TagBase[]) => void;
  canCreateNewTags?: boolean;
  sortBy?: TagsSortByOption;
  className?: string;
  triggerClassName?: string;
  hideChipsInInput?: boolean;
} & Omit<TagsSelectProps, 'onInputChange' | 'tags' | 'selectedTags' | 'onTagsSelectionChange'>;

export const PrivateTagsSelect = memo(
  ({
    selectedTags = [],
    onSelectedTagsChanged,
    canCreateNewTags,
    className,
    triggerClassName,
    hideChipsInInput = false,
    ...props
  }: PrivateTagsSelectProps) => {
    const { createTag } = useCreateTag();
    const [searchValue, setSearchValue] = useState('');
    const [error, setError] = useState('');
    const mounted = usePromise();
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canCreateTags = canCreateTag(permissions);

    const [debouncedSearchValue] = useDebounce(searchValue, SEARCH_DEBOUNCE_MS, {
      trailing: true,
    });

    const { data = [], isLoading: isInitialLoading } = usePrivateTags({
      limit: 100,
      isTagsSelect: true,
      search: debouncedSearchValue,
    });

    const onCreateTag = useCallback(
      async (name: string) => {
        try {
          setError('');
          onSelectedTagsChanged([...selectedTags, { id: '', label: name }]);
          const [{ id, label }] = await mounted(createTag(name, 'select'));
          onSelectedTagsChanged([...selectedTags, { id, label }]);
        } catch (error) {
          onSelectedTagsChanged(selectedTags);
          setError(getTagErrorMessage(error, name));
        }
      },
      [createTag, mounted, onSelectedTagsChanged, selectedTags],
    );

    const onSelectionChange = useCallback(
      (values: TagSelectOption[]) => {
        setError('');
        const baseTags = values.map(({ value, label }) => ({ id: value, label }));
        onSelectedTagsChanged(baseTags);
      },
      [onSelectedTagsChanged],
    );

    return (
      <div className={className}>
        <TagsSelect
          className={triggerClassName}
          tags={data}
          isLoading={isInitialLoading}
          selectedTags={selectedTags.map((tag) => convertTagToSelectOption(tag))}
          onTagsSelectionChange={onSelectionChange}
          onInputChange={setSearchValue}
          hideChipsInInput={hideChipsInInput}
          onCreateTag={canCreateNewTags && canCreateTags ? onCreateTag : undefined}
          {...props}
        />
        <span
          role="alert"
          className={classNames('absolute ads:text-error text-12 text-red-9', error ? 'block' : 'hidden')}
          aria-hidden={!error}
        >
          {capitalize(error)}
        </span>
      </div>
    );
  },
);

PrivateTagsSelect.displayName = 'PrivateTagsSelect';
